/**
 * System Status Overlay - Debug Panel for Monitoring App Health
 * 
 * Shows the loading sequence status like a "Check Engine" light.
 * Accessible via Ctrl+Alt+S (admin only) or always visible in dev mode.
 * 
 * Tracks:
 * - Static data loading (users, formTemplates, CPTs, etc.)
 * - Dynamic data loading (clients, appointments, etc.)
 * - Failed endpoints and retry counts
 * - Token/auth status
 * - WebSocket connection
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, RefreshCw, Wifi, WifiOff, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { useUserRoleName } from '../../hooks/useUserRoleName';

const SystemStatusOverlay = ({
  user,
  staticDataLoaded,
  dataHealth,
  wsConnected,
  onRefresh,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true); // Start minimized/collapsed by default
  const [loadTimes, setLoadTimes] = useState({});
  
  // Keyboard shortcut: Ctrl+Alt+S (Ctrl+Shift+D now belongs to the global date calculator)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.altKey && (e.key === 'S' || e.key === 's')) {
        e.preventDefault();
        setIsVisible(v => !v);
        if (!isVisible) {
          setIsMinimized(false); // Expand when toggled on via keyboard
        }
      }
      // ESC to close
      if (e.key === 'Escape' && isVisible) {
        setIsVisible(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible]);
  
  // Auto-show the minimized indicator in dev mode (but stay collapsed)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Show the minimized dot indicator in dev mode, but don't expand
      setIsVisible(true);
      setIsMinimized(true); // Keep collapsed
    }
  }, []);
  
  // Only auto-expand if there are CRITICAL errors (not just any errors)
  useEffect(() => {
    const criticalErrors = dataHealth?.failedEndpoints?.filter(e => 
      e.endpoint?.includes('/auth') || e.endpoint?.includes('/users/me')
    ) || [];
    if (criticalErrors.length > 0) {
      setIsVisible(true);
      setIsMinimized(false); // Only expand for critical auth errors
    }
  }, [dataHealth?.failedEndpoints]);
  
  // Only show for super admins in production
  const userRoleName = useUserRoleName();
  const isAdmin = userRoleName === 'admin' || userRoleName === 'super admin' || user?.is_super_admin;
  
  if (!isVisible) return null;
  if (process.env.NODE_ENV === 'production' && !isAdmin) return null;
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-400" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-400" />;
      case 'loading':
        return <Clock className="h-4 w-4 text-yellow-400 animate-pulse" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-orange-400" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };
  
  const getOverallStatus = () => {
    if (dataHealth?.failedEndpoints?.length > 0) return 'error';
    if (!staticDataLoaded) return 'loading';
    if (dataHealth?.staticData === 'success' && dataHealth?.dynamicData === 'success') return 'success';
    if (dataHealth?.staticData === 'loading' || dataHealth?.dynamicData === 'loading') return 'loading';
    return 'pending';
  };
  
  const overallStatus = getOverallStatus();
  
  // Minimized view - just a status indicator
  if (isMinimized) {
    return (
      <div 
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-4 right-4 z-[9999] cursor-pointer"
        title="Click to expand System Status (Ctrl+Alt+S)"
      >
        <div className={`
          flex items-center gap-2 px-3 py-2 rounded-full shadow-lg
          ${overallStatus === 'success' ? 'bg-green-900/90' : ''}
          ${overallStatus === 'loading' ? 'bg-yellow-900/90' : ''}
          ${overallStatus === 'error' ? 'bg-red-900/90 animate-pulse' : ''}
          ${overallStatus === 'pending' ? 'bg-gray-900/90' : ''}
        `}>
          {getStatusIcon(overallStatus)}
          <span className="text-xs text-white font-mono">
            {overallStatus === 'success' ? 'OK' : overallStatus.toUpperCase()}
          </span>
          {wsConnected ? (
            <Wifi className="h-3 w-3 text-green-400" />
          ) : (
            <WifiOff className="h-3 w-3 text-red-400" />
          )}
        </div>
      </div>
    );
  }
  
  // Full overlay view
  return (
    <div className="fixed bottom-4 right-4 z-[9999] w-80 max-h-[70vh] overflow-auto">
      <div className="bg-gray-900/95 text-gray-100 rounded-lg shadow-2xl border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700 bg-gray-800/50 rounded-t-lg">
          <div className="flex items-center gap-2">
            {getStatusIcon(overallStatus)}
            <span className="text-sm font-semibold">System Status</span>
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={() => setIsMinimized(true)}
              className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
              title="Minimize"
            >
              <span className="text-xs">—</span>
            </button>
            <button 
              onClick={() => setIsVisible(false)}
              className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
              title="Close (ESC)"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-3 space-y-3 text-xs font-mono">
          {/* User Info */}
          <div className="flex items-center justify-between text-gray-400">
            <span>User:</span>
            <span className="text-gray-200">{user?.email || 'Not logged in'}</span>
          </div>
          <div className="flex items-center justify-between text-gray-400">
            <span>Role:</span>
            <span className="text-gray-200">{user?.role || 'N/A'}</span>
          </div>
          
          {/* Divider */}
          <div className="border-t border-gray-700 pt-2">
            <div className="text-gray-500 mb-2">Load Sequence:</div>
          </div>
          
          {/* Static Data */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {getStatusIcon(dataHealth?.staticData || (staticDataLoaded ? 'success' : 'loading'))}
              <span>1. Static Data</span>
            </div>
            <span className={`
              ${staticDataLoaded ? 'text-green-400' : 'text-yellow-400'}
            `}>
              {staticDataLoaded ? 'LOADED' : 'LOADING...'}
            </span>
          </div>
          
          {/* Static Data Details */}
          {dataHealth?.staticDetails && (
            <div className="ml-6 text-gray-500 space-y-1">
              {Object.entries(dataHealth.staticDetails).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span>{key}:</span>
                  <span className={value > 0 ? 'text-green-400' : 'text-red-400'}>
                    {value} items
                  </span>
                </div>
              ))}
            </div>
          )}
          
          {/* Dynamic Data */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {getStatusIcon(dataHealth?.dynamicData || 'pending')}
              <span>2. Dynamic Data</span>
            </div>
            <span className={`
              ${dataHealth?.dynamicData === 'success' ? 'text-green-400' : ''}
              ${dataHealth?.dynamicData === 'loading' ? 'text-yellow-400' : ''}
              ${dataHealth?.dynamicData === 'error' ? 'text-red-400' : ''}
              ${!dataHealth?.dynamicData ? 'text-gray-500' : ''}
            `}>
              {dataHealth?.dynamicData?.toUpperCase() || 'WAITING'}
            </span>
          </div>
          
          {/* Dynamic Data Details */}
          {dataHealth?.dynamicDetails && (
            <div className="ml-6 text-gray-500 space-y-1">
              {Object.entries(dataHealth.dynamicDetails).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span>{key}:</span>
                  <span className={value > 0 ? 'text-green-400' : 'text-gray-400'}>
                    {value} items
                  </span>
                </div>
              ))}
            </div>
          )}
          
          {/* WebSocket Status */}
          <div className="flex items-center justify-between border-t border-gray-700 pt-2">
            <div className="flex items-center gap-2">
              {wsConnected ? (
                <Wifi className="h-4 w-4 text-green-400" />
              ) : (
                <WifiOff className="h-4 w-4 text-red-400" />
              )}
              <span>WebSocket</span>
            </div>
            <span className={wsConnected ? 'text-green-400' : 'text-red-400'}>
              {wsConnected ? 'CONNECTED' : 'DISCONNECTED'}
            </span>
          </div>
          
          {/* Auth Status */}
          <div className="flex items-center justify-between">
            <span>Auth Token:</span>
            <span className={sessionStorage.getItem('token') ? 'text-green-400' : 'text-red-400'}>
              {sessionStorage.getItem('token') ? 'PRESENT' : 'MISSING'}
            </span>
          </div>
          
          {/* Retry Count */}
          {dataHealth?.retryCount > 0 && (
            <div className="flex items-center justify-between text-orange-400">
              <span>Retries:</span>
              <span>{dataHealth.retryCount}</span>
            </div>
          )}
          
          {/* Failed Endpoints */}
          {dataHealth?.failedEndpoints?.length > 0 && (
            <div className="border-t border-red-900 pt-2 mt-2">
              <div className="text-red-400 mb-1 flex items-center gap-1">
                <XCircle className="h-3 w-3" />
                Failed Endpoints:
              </div>
              <div className="ml-4 space-y-1">
                {dataHealth.failedEndpoints.map((endpoint, i) => (
                  <div key={i} className="text-red-300 truncate" title={endpoint}>
                    • {endpoint.replace('/api/', '')}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-gray-700">
            <button
              onClick={() => {
                if (onRefresh) onRefresh();
                else window.location.reload();
              }}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-200"
            >
              <RefreshCw className="h-3 w-3" />
              Refresh
            </button>
            <button
              onClick={() => {
                void 0 && console.log('[SystemStatus] Current state:', { user, staticDataLoaded, dataHealth, wsConnected });
                void 0 && console.log('[SystemStatus] SessionStorage token:', sessionStorage.getItem('token') ? 'Present' : 'Missing');
              }}
              className="flex-1 px-2 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-200"
            >
              Log to Console
            </button>
          </div>
          
          {/* Shortcut hint */}
          <div className="text-center text-gray-600 text-[10px] pt-1">
            Ctrl+Alt+S to toggle • ESC to close
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemStatusOverlay;
